import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCalendarPlus, LuInfo, LuLoaderCircle, LuSave } from 'react-icons/lu';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './AddVisitModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { Field, SelectInput, TextArea, TextInput, ToggleTile } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { normalizePatientId } from '../../utils/patientAccess';

const FORM_ID = 'add-visit-form';
const DAY_MS = 1000 * 60 * 60 * 24;
const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];
const REPEAT_UNITS = ['day', 'week', 'month'];
const EMAIL_NOTIFICATION_URL = 'https://us-central1-habcare-8ecad.cloudfunctions.net/sendEmployeeVisitNotification';

const daysBetween = (from, to) => Math.ceil((new Date(to) - new Date(from)) / DAY_MS);

/**
 * "Yeni Visit Ekle": one visit, or a repeating series up to a closing date,
 * for a staff member of the project. The visit window (start / end around
 * the visit day) is stored as a period ("-3,+2"); the visit number is
 * suggested from the patient's visits in this project. The staff member is
 * notified by e-mail, and the visit can be added to Google Calendar.
 */
const AddVisitModal = ({ project, onClose, onVisitAdded }) => {
  const { t, language, locale } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [visitPeriodBefore, setVisitPeriodBefore] = useState('');
  const [visitPeriodAfter, setVisitPeriodAfter] = useState('');
  const [patientId, setPatientId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [visitNumber, setVisitNumber] = useState('');
  // Proje + hastaya göre hesaplanan sıradaki vizit numarası (öneri)
  const [suggestedVisitNumber, setSuggestedVisitNumber] = useState(null);
  const [visitInfo, setVisitInfo] = useState('');
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenFail, setScreenFail] = useState(false);
  const [willRepeat, setWillRepeat] = useState(false);
  const [visitEndDate, setVisitEndDate] = useState('');
  const [visitRepeatPeriod, setVisitRepeatPeriod] = useState('');
  const [visitRepeatUnit, setVisitRepeatUnit] = useState('day');
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchProjectEmployees = async () => {
      try {
        const projectData = (await getDoc(doc(db, 'Projects', project.id))).data();
        let employees = [];
        if (projectData && projectData.employees && typeof projectData.employees === 'object') {
          employees = Object.values(projectData.employees);
        }
        setAssignedEmployees(employees);
      } catch (error) {
        console.error('Error fetching project employees:', error);
        setAssignedEmployees([]);
      }
    };

    const fetchPatients = async () => {
      setLoadingPatients(true);
      try {
        const snapshot = await getDocs(query(collection(db, 'Patients'), where('projectId', '==', project.id)));
        setProjectPatients(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching patients:', error);
        setProjectPatients([]);
      } finally {
        setLoadingPatients(false);
      }
    };

    fetchProjectEmployees();
    fetchPatients();
  }, [project.id]);

  /**
   * Kaçıncı vizit olduğunu, aynı hastanın bu projedeki en yüksek vizit
   * numarasının bir fazlası olarak önerir; kaydı yoksa 1. Alan serbesttir.
   * Sorgu projectId ile yapılır, hasta eşleşmesi istemcide normalize edilerek
   * kontrol edilir; böylece farklı yazılmış hasta kimlikleri öneriyi kaçırmaz.
   */
  useEffect(() => {
    const normalizedPatient = normalizePatientId(patientId);
    if (!project?.id || !normalizedPatient) {
      setSuggestedVisitNumber(null);
      return undefined;
    }
    let cancelled = false;
    const suggestVisitNumber = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'Visits'), where('projectId', '==', project.id)));
        if (cancelled) return;
        const numbers = snapshot.docs
          .map((d) => d.data())
          .filter((v) => normalizePatientId(v.patientId) === normalizedPatient)
          .map((v) => Number(v.visitNumber))
          .filter((n) => Number.isFinite(n) && n > 0);
        const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
        setSuggestedVisitNumber(next);
        setVisitNumber(String(next));
      } catch (error) {
        console.error('Vizit numarası önerilemedi:', error);
        if (!cancelled) setSuggestedVisitNumber(null);
      }
    };
    suggestVisitNumber();
    return () => {
      cancelled = true;
    };
  }, [project?.id, patientId]);

  const createGoogleCalendarUrl = (projectName) => {
    try {
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      endDate.setDate(endDate.getDate() + 1);
      const formatDateForGCal = (date) => `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      const eventTitle = encodeURIComponent(`Ziyaret: ${projectName}`);
      const eventLocation = encodeURIComponent('');
      const eventDetails = encodeURIComponent(`Hasta ID: ${patientId || 'Belirtilmemiş'}\n${visitInfo || ''}`);
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${formatDateForGCal(startDate)}/${formatDateForGCal(endDate)}&details=${eventDetails}&location=${eventLocation}&sf=true&output=xml`;
    } catch (error) {
      console.error('Error creating calendar URL:', error);
      return '';
    }
  };

  const clearError = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const required = t('dataTable.form.required');
    const found = {};
    if (!selectedEmployee) found.employee = required;
    if (!visitDate) found.visitDate = required;
    if (!visitPeriodBefore) found.visitPeriodBefore = required;
    if (!visitPeriodAfter) found.visitPeriodAfter = required;
    if (willRepeat) {
      if (!visitEndDate) found.visitEndDate = required;
      if (!visitRepeatPeriod) found.visitRepeatPeriod = required;
    }
    return found;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const datesMissing = found.visitDate || found.visitPeriodBefore || found.visitPeriodAfter;
      toast.error(datesMissing ? t('projectManagement.addVisit.errors.dates') : t('projectManagement.form.errors.required'));
      return;
    }

    setIsSubmitting(true);
    try {
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      const firstVisit = new Date(visitDate);
      const daysBefore = Math.ceil((firstVisit - startDate) / DAY_MS);
      const daysAfter = Math.ceil((endDate - firstVisit) / DAY_MS);

      if (startDate > firstVisit || endDate < firstVisit) {
        toast.error(t('projectManagement.addVisit.errors.window'));
        setIsSubmitting(false);
        return;
      }

      const periodString = daysBefore > 0 || daysAfter > 0 ? `-${daysBefore},+${daysAfter}` : '0';
      const totalDays = daysBefore + daysAfter;
      const employeeObj = assignedEmployees.find((emp) => emp.id === selectedEmployee);

      const baseVisitData = {
        projectId: project.id,
        employeeId: selectedEmployee,
        employeeName: employeeObj ? `${employeeObj.name} ${employeeObj.surname}` : '',
        period: periodString,
        patientId: normalizePatientId(patientId),
        visitType: visitType || '',
        visitNumber: visitNumber ? Number(visitNumber) : '',
        info: visitInfo,
        createdAt: new Date(),
        preVisitForm: false,
        screenFail: visitType === 'hastane' && screenFail,
      };

      const startDateTime = new Date(visitDate);
      const visitPromises = [];

      if (willRepeat) {
        const endDateTime = new Date(visitEndDate);
        const repeatPeriod = Number(visitRepeatPeriod);
        // Periyot 0/geçersizse tarih hiç ilerlemez ve döngü sonsuza girerdi.
        if (!Number.isFinite(repeatPeriod) || repeatPeriod <= 0) {
          toast.error(t('projectManagement.addVisit.errors.repeatPeriod'));
          setIsSubmitting(false);
          return;
        }
        // Tekrar eden ziyaretler sıradaki ziyaretlerdir: numara her tekrarda
        // bir artar (2. Visit, 3. Visit ...). Numara girilmemişse boş kalır.
        const startNumber = visitNumber ? Number(visitNumber) : null;
        let repeatIndex = 0;
        let currentVisitDate = new Date(startDateTime);
        while (currentVisitDate <= endDateTime) {
          visitPromises.push(addDoc(collection(db, 'Visits'), {
            ...baseVisitData,
            visitNumber: startNumber !== null ? startNumber + repeatIndex : '',
            firstVisitDate: currentVisitDate.toISOString().split('T')[0],
          }));
          repeatIndex += 1;
          currentVisitDate = new Date(currentVisitDate);
          if (visitRepeatUnit === 'week') {
            currentVisitDate.setDate(currentVisitDate.getDate() + (7 * repeatPeriod));
          } else if (visitRepeatUnit === 'month') {
            currentVisitDate.setMonth(currentVisitDate.getMonth() + repeatPeriod);
          } else {
            currentVisitDate.setDate(currentVisitDate.getDate() + repeatPeriod + totalDays);
          }
        }
      } else {
        visitPromises.push(addDoc(collection(db, 'Visits'), {
          ...baseVisitData,
          firstVisitDate: startDateTime.toISOString().split('T')[0],
        }));
      }

      await Promise.all(visitPromises);

      // Çalışana e-posta bildirimi (başarısız olsa da kayıt tamamdır)
      try {
        const response = await fetch(EMAIL_NOTIFICATION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Origin: window.location.origin },
          body: JSON.stringify({
            employeeId: selectedEmployee,
            visitDate,
            projectName: project.titleShort || 'Not specified',
            period: periodString,
            info: visitInfo || '',
          }),
        });
        if (!response.ok) console.error('Email notification failed:', await response.json());
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }

      if (addToCalendar) {
        const calendarUrl = createGoogleCalendarUrl(project.titleShort || project.title || 'Ziyaret');
        if (calendarUrl) window.open(calendarUrl, '_blank');
      }

      toast.success(t('projectManagement.addVisit.toast.added'));
      onVisitAdded();
      onClose();
    } catch (error) {
      console.error('Error adding visit:', error);
      toast.error(t('projectManagement.addVisit.toast.addError'));
      setIsSubmitting(false);
    }
  };

  const label = (key) => t(`projectManagement.addVisit.${key}`);
  const windowReady = visitDate && visitPeriodBefore && visitPeriodAfter;

  let patientControl;
  if (loadingPatients) {
    patientControl = (
      <p className="tw-flex tw-h-10 tw-items-center tw-gap-2 tw-px-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
        {label('patientsLoading')}
      </p>
    );
  } else if (projectPatients.length > 0) {
    patientControl = (
      <SelectInput
        id="add-visit-patient"
        value={patientId}
        onChange={(event) => setPatientId(event.target.value)}
        placeholder={label('patientOptional')}
        options={projectPatients.map((patient) => ({
          value: patient.patientId,
          label: `${patient.patientId}${patient.status ? ` (${patient.status})` : ''}`,
        }))}
      />
    );
  } else {
    patientControl = <p className="tw-flex tw-h-10 tw-items-center tw-px-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('noPatients')}</p>;
  }

  return (
    <Modal
      isOpen
      onClose={() => !isSubmitting && onClose()}
      size="lg"
      title={label('title')}
      subtitle={project.titleShort || undefined}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30" size="xs">
          <LuCalendarPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting} className="max-sm:tw-hidden">{t('dataTable.form.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={isSubmitting} className="max-sm:tw-flex-1">
            {isSubmitting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSubmitting ? label('submitting') : label('submit')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} noValidate onSubmit={handleSubmit} className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('employee')} htmlFor="add-visit-employee" required error={errors.employee}>
          <SelectInput
            id="add-visit-employee"
            value={selectedEmployee}
            onChange={(event) => {
              setSelectedEmployee(event.target.value);
              clearError('employee');
            }}
            placeholder={label('choose')}
            error={errors.employee}
            options={assignedEmployees.filter((employee) => employee.id).map((employee) => ({
              value: employee.id,
              label: `${employee.name || ''} ${employee.surname || ''}`.trim(),
            }))}
          />
        </Field>
        <Field label={label('patient')} htmlFor="add-visit-patient">
          {patientControl}
        </Field>

        <Field label={label('visitDate')} htmlFor="add-visit-date" required error={errors.visitDate}>
          <TextInput
            id="add-visit-date"
            type="date"
            value={visitDate}
            onChange={(event) => {
              setVisitDate(event.target.value);
              clearError('visitDate');
            }}
            error={errors.visitDate}
          />
        </Field>
        <Field label={label('visitType')} htmlFor="add-visit-type">
          <SelectInput
            id="add-visit-type"
            value={visitType}
            onChange={(event) => setVisitType(event.target.value)}
            placeholder={label('choose')}
            options={VISIT_TYPES.map((type) => ({ value: type, label: t(`projectManagement.addVisit.types.${type}`) }))}
          />
        </Field>

        {/* Visit window */}
        <fieldset className="tw-col-span-full tw-m-0 tw-min-w-0 tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-p-3 dark:tw-border-white/10">
          <legend className="tw-px-1 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('window')}</legend>
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <Field label={label('windowStart')} htmlFor="add-visit-before" required error={errors.visitPeriodBefore}>
              <TextInput
                id="add-visit-before"
                type="date"
                value={visitPeriodBefore}
                max={visitDate || undefined}
                onChange={(event) => {
                  setVisitPeriodBefore(event.target.value);
                  clearError('visitPeriodBefore');
                }}
                error={errors.visitPeriodBefore}
              />
            </Field>
            <Field label={label('windowEnd')} htmlFor="add-visit-after" required error={errors.visitPeriodAfter}>
              <TextInput
                id="add-visit-after"
                type="date"
                value={visitPeriodAfter}
                min={visitDate || undefined}
                onChange={(event) => {
                  setVisitPeriodAfter(event.target.value);
                  clearError('visitPeriodAfter');
                }}
                error={errors.visitPeriodAfter}
              />
            </Field>
          </div>
          {windowReady && (
            <div className="tw-rounded-xl tw-bg-blue-500/[0.08] tw-px-3 tw-py-2 tw-text-xs tw-text-blue-900 dark:tw-text-blue-100">
              <p className="tw-font-semibold">
                {label('windowVisit')}{' '}
                {new Date(visitDate).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="tw-mt-0.5 tw-text-blue-800/80 dark:tw-text-blue-200/80">
                {t('projectManagement.addVisit.windowDetail', {
                  before: daysBetween(visitPeriodBefore, visitDate),
                  after: daysBetween(visitDate, visitPeriodAfter),
                  total: daysBetween(visitPeriodBefore, visitPeriodAfter),
                })}
              </p>
            </div>
          )}
        </fieldset>

        <Field
          label={label('visitNumber')}
          htmlFor="add-visit-number"
          hint={suggestedVisitNumber !== null ? (
            String(suggestedVisitNumber) === String(visitNumber)
              ? t('projectManagement.addVisit.suggestedSame', { n: suggestedVisitNumber })
              : t('projectManagement.addVisit.suggested', { n: suggestedVisitNumber })
          ) : undefined}
        >
          <TextInput
            id="add-visit-number"
            type="number"
            min="1"
            inputMode="numeric"
            value={visitNumber}
            onChange={(event) => setVisitNumber(event.target.value)}
            placeholder={label('visitNumberPlaceholder')}
          />
        </Field>
        {visitType === 'hastane' ? (
          <Field label={label('screenFail')} htmlFor="add-visit-screen-fail" hint={screenFail ? label('screenFailNote') : undefined}>
            <ToggleTile
              id="add-visit-screen-fail"
              label={screenFail ? t('projectManagement.yes') : t('projectManagement.no')}
              checked={screenFail}
              onChange={setScreenFail}
            />
          </Field>
        ) : <span aria-hidden="true" className="tw-hidden sm:tw-block" />}

        <Field label={label('info')} htmlFor="add-visit-info" className="sm:tw-col-span-2">
          <TextArea id="add-visit-info" rows={2} value={visitInfo} onChange={(event) => setVisitInfo(event.target.value)} />
        </Field>

        <Field label={label('repeat')} htmlFor="add-visit-repeat">
          <ToggleTile
            id="add-visit-repeat"
            label={willRepeat ? t('projectManagement.yes') : t('projectManagement.no')}
            checked={willRepeat}
            onChange={setWillRepeat}
          />
        </Field>
        <Field label={label('calendar')} htmlFor="add-visit-calendar">
          <ToggleTile
            id="add-visit-calendar"
            label={addToCalendar ? t('projectManagement.yes') : t('projectManagement.no')}
            checked={addToCalendar}
            onChange={setAddToCalendar}
          />
        </Field>

        {willRepeat && (
          <>
            <Field label={label('endDate')} htmlFor="add-visit-end" required error={errors.visitEndDate}>
              <TextInput
                id="add-visit-end"
                type="date"
                value={visitEndDate}
                min={visitDate || undefined}
                onChange={(event) => {
                  setVisitEndDate(event.target.value);
                  clearError('visitEndDate');
                }}
                error={errors.visitEndDate}
              />
            </Field>
            <Field label={label('repeatPeriod')} htmlFor="add-visit-repeat-period" required error={errors.visitRepeatPeriod}>
              <div className="tw-grid tw-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] tw-gap-2">
                <TextInput
                  id="add-visit-repeat-period"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={visitRepeatPeriod}
                  onChange={(event) => {
                    setVisitRepeatPeriod(event.target.value);
                    clearError('visitRepeatPeriod');
                  }}
                  error={errors.visitRepeatPeriod}
                />
                <SelectInput
                  value={visitRepeatUnit}
                  onChange={(event) => setVisitRepeatUnit(event.target.value)}
                  aria-label={label('repeatUnit')}
                  options={REPEAT_UNITS.map((unit) => ({ value: unit, label: t(`projectManagement.addVisit.units.${unit}`) }))}
                />
              </div>
            </Field>
            <p className="tw-col-span-full tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
              <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              {label('repeatHint')}
            </p>
          </>
        )}
      </form>
    </Modal>
  );
};

AddVisitModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onVisitAdded: PropTypes.func.isRequired,
};

export default AddVisitModal;
